import { ApiError } from '../../../../lib/apiError';
import { pressReleasesApi } from '../pressReleaseApi';
import { drawThumbnail } from './drawThumbnail';

/**
 * A picture the writer placed in the content BEFORE it was uploaded is held by the form under a
 * PENDING id, and uploaded only when the form is submitted — so a form abandoned leaves nothing
 * behind.
 *
 * **The id is deliberately NOT GUID-shaped** (`pending:1`). `attachmentIdsIn` accepts a GUID or
 * nothing, so a pending id that ever reached a stored document would draw no picture and name no
 * URL — it fails CLOSED. The submit never lets one reach the server anyway: each is swapped for its
 * real attachment id, or the picture is taken out.
 */
export const PENDING_PREFIX = 'pending:';

export function isPendingPicture(id: string): boolean {
  return id.startsWith(PENDING_PREFIX);
}

/** The pending pictures the document still PLACES — one deleted in the editor is not uploaded. */
export function pendingPicturesIn(html: string): string[] {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  return Array.from(doc.querySelectorAll('img[data-attachment]'))
    .map((img) => img.getAttribute('data-attachment') ?? '')
    .filter(isPendingPicture);
}

/**
 * The document with every pending picture swapped for its uploaded id, or TAKEN OUT where it has
 * none (not uploaded yet, or refused). Parsed, never regexed — the same as `attachmentIdsIn`.
 */
export function placeUploadedPictures(html: string, uploaded: Record<string, string>): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('img[data-attachment]').forEach((img) => {
    const id = img.getAttribute('data-attachment') ?? '';
    if (!isPendingPicture(id)) return;

    const attachmentId = uploaded[id];
    if (attachmentId) img.setAttribute('data-attachment', attachmentId);
    else img.remove();
  });

  // An empty document is `<p></p>`, never an empty string.
  return doc.body.innerHTML || '<p></p>';
}

/**
 * Uploads the pending pictures the document places, ONE request each, recording each real id in
 * `uploaded` as it lands — the map outlives a failed submit, so a retry never files one twice.
 * What failed is returned, named, for the form to report.
 */
export async function uploadPendingPictures(
  releaseId: string,
  pendingIds: string[],
  files: Record<string, File>,
  uploaded: Record<string, string>,
  onProgress: (percent: number) => void,
): Promise<{ name: string; reason: string }[]> {
  const failed: { name: string; reason: string }[] = [];
  const todo = pendingIds.filter((id) => !uploaded[id] && files[id]);

  for (const [index, id] of todo.entries()) {
    const file = files[id];

    try {
      const thumbnail = await drawThumbnail(file);
      const attachment = await pressReleasesApi.attach(releaseId, file, thumbnail, (percent) =>
        onProgress(Math.floor(((index + percent / 100) / todo.length) * 100)),
      );

      uploaded[id] = attachment.id;
    } catch (caught) {
      failed.push({ name: file.name, reason: caught instanceof ApiError ? caught.message : 'It could not be uploaded.' });
    }
  }

  return failed;
}
